/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Spinner} from "react-bootstrap";
import {HorizontalTabs} from "../../../components/Tabs/HorizontalTabs";
import React, {useEffect, useState} from 'react';
import {useSearchParams} from "react-router-dom";
import {useAppSelector} from "../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import {Toolbar, Typography} from '@mui/material';
import {usePostRequest} from "../../../../api/helper";
import {fetchRealWorldObjectsEndpoint, potentiallyMovingObjectsTitle} from "../../objectApp/constants";
import {ETrackedItemTypes} from "../../../../constants/trackedItems";
import PotentiallyMovingObjectsDetailsTab from "./PotentiallyMovingObjectsDetailsTab";


const PotentiallyMovingObjectDetails = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tabs, setTabs] = useState<any>([])
  const [title, setTitle] = useState("");
  const [searchParams, _] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const [renderKey, setRenderKey] = useState(0);
  const [distinctObjectClassification, setDistinctObjectClassification] = useState('');

  // Function to trigger re-render
  const triggerReRender = () => {
    setRenderKey(prevKey => prevKey + 1);
  };

  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const objectId = params?.id

  useEffect(() => {
    fetchBaselineDataRWO();
  }, [renderKey]);

  const fetchBaselineDataHandleRWO = usePostRequest(fetchRealWorldObjectsEndpoint)
  const fetchBaselineDataRWO = () => {
    setIsLoading(true);

    fetchBaselineDataHandleRWO({id: objectId})
      .then((data) => {

        // Create a Set to store distinct object classifications
        const distinctObjectClassifications = new Set<string>();

        // Collect distinct object classifications from matching objects
        data.forEach((object: { objectClassification: any }) => {
          if (typeof object.objectClassification === 'string') {
            object.objectClassification.split(',')
              .map(classification => classification.trim())  // Trim whitespace
              .forEach(classification => distinctObjectClassifications.add(classification));
          }
        });

        // Convert the Set to a comma-separated string
        const distinctObjectClassificationsString = Array.from(distinctObjectClassifications).join(', ');

        // Set the string in state
        if (distinctObjectClassificationsString) { // Checks if the string is not empty or null
          setDistinctObjectClassification(distinctObjectClassificationsString);
        }

        // Find the object by ID
        const filteredData = data.find((item: { id: string }) => item.id === objectId);

        if (filteredData) {
          const objectName = filteredData.objectName;
          const objectType = filteredData.type;

          // Filter data again to find all instances with the same objectName
          const matchingObjects = data.filter((item: { objectName: string, type: string }) => item.objectName === objectName && item.type === objectType);

          // Update all matching objects' objectClassification with distinctObjectClassificationsString
          matchingObjects.forEach((object: { objectClassificationList: string; }) => {
            object.objectClassificationList = distinctObjectClassificationsString;
          });

          processData(matchingObjects); // Process the filtered objects

          // Return the filtered objects
          return matchingObjects;

        }
      })
      .finally(() => setIsLoading(false));
  }

  const processData = (objectData: any) => {
    const localTabs: any[] = []
    const type = objectData[0].objectName
    setTitle(`Potentially Moving Objects - ${type} Properties`);

    const potentiallyMovingObjectDetailsContent = <PotentiallyMovingObjectsDetailsTab
      objectData={objectData}
      fetchBaselineData={fetchBaselineDataRWO}
      triggerReRender={triggerReRender}
      isCustomObject={type === "Custom"}
    />

    localTabs.push({'name': 'Potentially Moving Object Details', 'content': potentiallyMovingObjectDetailsContent})
    setTabs(localTabs)
  }

  const dialogState = useAppSelector(selectGlobalDialogState); // Call the hook outside

  /**
   * Trigger refresh when dialog is closed
   */
  const triggerPMORefresh = () => {
    const event = new Event('refreshPMOTable');
    window.dispatchEvent(event);
  };
  useEffect(() => {
    if (!dialogState.isOpen) {
      triggerPMORefresh(); // Trigger refresh when dialog is closed
    }
  }, [dialogState]); // Add dialogState to the dependency array

  /**
   * Dialog header icons
   */
  const icons = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <CloseGlobalDialogButton/>
    </div>
  )

  /**
   * Set the title of the browser tab
   */
  useEffect(() => {
    document.title = title;
    return () => {
      document.title = potentiallyMovingObjectsTitle + ' - Landing';

    };
  }, [title]);

  return (
    <>
      <div style={{height: '100%', width: '100%', display: 'flex', flexFlow: 'column nowrap', overflow: 'auto'}}>
        <div>
          <Toolbar variant="dense" style={{justifyContent: "space-between", background: '#3481d5', height: "100%"}}>
            <Typography sx={{color: '#ffffff', fontSize: 14}}>{title}</Typography>
            {icons}
          </Toolbar>
        </div>
        {isLoading ?
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
            <Spinner animation={'border'} style={{color: 'grey'}}/>
          </div>
          :
          <div style={{height: '100%', overflow: 'auto', display: 'flex', justifyContent: 'space-between'}}>
            <HorizontalTabs initContent={null} initHtabIndex={0} horizontalTabs={tabs} align={'left'}
                            favoriteType={ETrackedItemTypes.OBJECT} uid={'htabs-1'}/>
          </div>
        }
      </div>
    </>
  )
}
export default PotentiallyMovingObjectDetails

